import { Box, Button, Chip, TextField, Typography } from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";

import useCanRespondToDecisions from "../../../../../hooks/useCanRespondToDecisions";
import { respondToRuleEngineDecision } from "../../../../../redux/slices/store_analytics";

const isPpcType = (type) =>
  type === "ppc_new_campaign" ||
  type === "ppc_increase_budget" ||
  type === "ppc_resume_campaign" ||
  type === "ppc_add_to_campaign" ||
  type === "ppc_enroll_bid_optimizer";

const formatPercent = (value) =>
  value != null ? `${(value * 100).toFixed(1)}%` : "—";

const formatNumber = (value) => (value != null ? value.toLocaleString() : "—");

const getActionLabel = (recommendationType) => {
  switch (recommendationType) {
    case "ppc_new_campaign":
      return "Create exact campaign";
    case "ppc_increase_budget":
      return "Increase campaign budget";
    case "ppc_resume_campaign":
      return "Resume campaign";
    case "ppc_add_to_campaign":
      return "Add ASIN to existing campaign";
    case "ppc_enroll_bid_optimizer":
      return "Optimize Bid / Placement";
    case "listing_bullets":
      return "Add to bullets";
    case "listing_backend_keywords":
      return "Add to backend keywords";
    default:
      return "Take action";
  }
};

const ActionRow = ({
  decision,
  asin,
  store,
  marketplace,
  userAllowedToRespond,
  onResponded,
}) => {
  const dispatch = useDispatch();
  const inputRef = useRef(null);
  const [showRejectInput, setShowRejectInput] = useState(false);
  const [showAcceptInput, setShowAcceptInput] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [acceptReason, setAcceptReason] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [responded, setResponded] = useState(false);

  const agentName = (decision.decision?.agent || "sam").toLowerCase();
  const statusKey = `${decision.execution_id}_${asin}_${agentName}`;
  const recommendationType = decision.decision?.recommendation_type || "";
  const isPpc = isPpcType(recommendationType);

  const handleResponse = async (userResponse, reason) => {
    const payload = {
      asin,
      sku: decision.sku,
      store,
      execution_id: decision.execution_id,
      status_key: statusKey,
      marketplace,
      user_response: userResponse,
      decision_type: "sam",
    };
    if (userResponse === "Accept" && reason) payload.accept_reason = reason;
    if (userResponse === "Reject" && reason) payload.reject_reason = reason;

    setIsLoading(true);
    await dispatch(respondToRuleEngineDecision(payload));
    setIsLoading(false);
    setResponded(true);
    onResponded(decision.execution_id);
  };

  if (responded) return null;

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        p: 1.5,
        backgroundColor: "#fafafa",
        borderRadius: 2,
        border: "1px solid #f0f0f0",
      }}
    >
      <Chip
        label={isPpc ? "PPC" : "SEO"}
        size="small"
        sx={{
          backgroundColor: isPpc ? "#e3f2fd" : "#e8f5e9",
          color: isPpc ? "#1565c0" : "#2e7d32",
          fontWeight: 600,
          fontSize: "0.68rem",
          height: 20,
          flexShrink: 0,
        }}
      />
      <Box sx={{ flex: 1 }}>
        <Typography variant="body2" sx={{ fontWeight: 500, color: "#333" }}>
          {getActionLabel(recommendationType)}
        </Typography>
        {(recommendationType === "ppc_increase_budget" ||
          recommendationType === "ppc_resume_campaign" ||
          recommendationType === "ppc_add_to_campaign" ||
          recommendationType === "ppc_enroll_bid_optimizer") &&
          (decision.decision?.campaign_name ||
            decision.decision?.current_acos != null) && (
            <Typography variant="caption" sx={{ color: "#888" }}>
              {decision.decision?.campaign_name || ""}
              {decision.decision?.campaign_name &&
                decision.decision?.current_acos != null &&
                " · "}
              {decision.decision?.current_acos != null &&
                `ACOS: ${formatPercent(decision.decision.current_acos)}`}
            </Typography>
          )}
      </Box>

      {isLoading ? (
        <Typography variant="caption" sx={{ color: "#999" }}>
          Processing...
        </Typography>
      ) : showAcceptInput ? (
        <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
          <TextField
            inputRef={inputRef}
            size="small"
            placeholder="Reason (optional)"
            value={acceptReason}
            onChange={(e) => setAcceptReason(e.target.value)}
            onKeyDown={(e) =>
              e.key === "Enter" && handleResponse("Accept", acceptReason)
            }
            sx={{ width: 160 }}
          />
          <Button
            size="small"
            variant="contained"
            onClick={() => handleResponse("Accept", acceptReason)}
            sx={{
              backgroundColor: "#4caf50",
              textTransform: "none",
              boxShadow: "none",
              borderRadius: 3,
              "&:hover": { backgroundColor: "#45a049", boxShadow: "none" },
            }}
          >
            Confirm
          </Button>
          <Button
            size="small"
            variant="text"
            onClick={() => {
              setShowAcceptInput(false);
              setAcceptReason("");
            }}
            sx={{ color: "#666", textTransform: "none" }}
          >
            Cancel
          </Button>
        </Box>
      ) : showRejectInput ? (
        <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
          <TextField
            inputRef={inputRef}
            size="small"
            placeholder="Reason (optional)"
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            onKeyDown={(e) =>
              e.key === "Enter" && handleResponse("Reject", rejectReason)
            }
            sx={{ width: 160 }}
          />
          <Button
            size="small"
            variant="contained"
            onClick={() => handleResponse("Reject", rejectReason)}
            sx={{
              backgroundColor: "#f44336",
              textTransform: "none",
              boxShadow: "none",
              borderRadius: 3,
              "&:hover": { backgroundColor: "#d32f2f", boxShadow: "none" },
            }}
          >
            Confirm
          </Button>
          <Button
            size="small"
            variant="text"
            onClick={() => {
              setShowRejectInput(false);
              setRejectReason("");
            }}
            sx={{ color: "#666", textTransform: "none" }}
          >
            Cancel
          </Button>
        </Box>
      ) : (
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            variant="outlined"
            size="small"
            disabled={!userAllowedToRespond}
            onClick={() => {
              if (isPpc) {
                setShowAcceptInput(true);
                setShowRejectInput(false);
                setTimeout(() => inputRef.current?.focus(), 0);
              } else {
                handleResponse("Accept");
              }
            }}
            sx={{
              borderColor: "#4caf50",
              color: "#4caf50",
              textTransform: "none",
              fontWeight: 500,
              borderRadius: 3,
              "&:hover": {
                borderColor: "#45a049",
                backgroundColor: "transparent",
              },
              "&.Mui-disabled": { borderColor: "#e0e0e0", color: "#9e9e9e" },
            }}
          >
            Accept
          </Button>
          <Button
            variant="outlined"
            size="small"
            disabled={!userAllowedToRespond}
            onClick={() => {
              setShowRejectInput(true);
              setShowAcceptInput(false);
              setTimeout(() => inputRef.current?.focus(), 0);
            }}
            sx={{
              borderColor: "#f44336",
              color: "#f44336",
              textTransform: "none",
              fontWeight: 500,
              borderRadius: 3,
              "&:hover": {
                borderColor: "#d32f2f",
                backgroundColor: "transparent",
              },
              "&.Mui-disabled": { borderColor: "#e0e0e0", color: "#9e9e9e" },
            }}
          >
            Reject
          </Button>
        </Box>
      )}
    </Box>
  );
};

const SamDecisionCard = ({
  asin,
  store,
  marketplace,
  samDecisions,
  onRemove,
}) => {
  const [respondedIds, setRespondedIds] = useState(new Set());

  const userAllowedToRespond = useCanRespondToDecisions(store);

  // All decisions in a group share the same keyword and metrics
  const firstDecision = samDecisions[0]?.decision || {};
  const keyword = firstDecision.keyword;
  const sqpScore = firstDecision.sqp_score;
  const searchVolume = firstDecision.search_volume;
  const cvr = firstDecision.cvr;
  const asinSaleShare = firstDecision.asin_sale_share;

  useEffect(() => {
    if (
      respondedIds.size > 0 &&
      respondedIds.size >= samDecisions.length &&
      onRemove
    ) {
      const timer = setTimeout(onRemove, 300);
      return () => clearTimeout(timer);
    }
  }, [respondedIds, samDecisions.length, onRemove]);

  const handleResponded = (executionId) => {
    setRespondedIds((prev) => new Set([...prev, executionId]));
  };

  return (
    <Box sx={{ p: 2, width: "100%" }}>
      {/* Keyword */}
      <Typography
        variant="body1"
        sx={{ fontWeight: 700, fontSize: "0.9125rem", mb: 1.5 }}
      >
        &ldquo;{keyword}&rdquo;
      </Typography>

      {/* Metrics row */}
      <Box
        sx={{
          display: "flex",
          gap: 3,
          mb: 2,
          pb: 1.5,
          borderBottom: "1px solid #f0f0f0",
          flexWrap: "wrap",
        }}
      >
        <MetricItem label="SQP score" value={sqpScore ?? "—"} />
        <MetricItem label="Search vol" value={formatNumber(searchVolume)} />
        <MetricItem
          label="Our CVR"
          value={formatPercent(cvr)}
          color={cvr != null && cvr > 0.1 ? "#4caf50" : undefined}
        />
        <MetricItem
          label="Sale share"
          value={formatPercent(asinSaleShare)}
          color={
            asinSaleShare != null && asinSaleShare < 0.05
              ? "#ef6c00"
              : undefined
          }
        />
      </Box>

      {/* Recommended actions */}
      <Typography
        variant="caption"
        sx={{
          color: "#999",
          mb: 1,
          display: "block",
          fontWeight: 600,
          textTransform: "uppercase",
          fontSize: "0.65rem",
          letterSpacing: "0.05em",
        }}
      >
        Recommended actions
      </Typography>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        {samDecisions.map((d) => (
          <ActionRow
            key={d.execution_id}
            decision={d}
            asin={asin}
            store={store}
            marketplace={marketplace}
            userAllowedToRespond={userAllowedToRespond}
            onResponded={handleResponded}
          />
        ))}
      </Box>

      {!userAllowedToRespond && (
        <Typography
          variant="body2"
          sx={{
            color: "red",
            fontStyle: "italic",
            mt: 1,
            textAlign: "center",
            fontSize: "0.75rem",
          }}
        >
          Only the store owner can accept or reject recommendations.
        </Typography>
      )}
    </Box>
  );
};

const MetricItem = ({ label, value, color }) => (
  <Box sx={{ display: "flex", flexDirection: "column" }}>
    <Typography
      variant="caption"
      sx={{ color: "#9e9e9e", fontSize: "0.68rem", lineHeight: 1.2 }}
    >
      {label}
    </Typography>
    <Typography
      variant="body2"
      sx={{ fontWeight: 600, color: color || "#374151", fontSize: "0.85rem" }}
    >
      {value}
    </Typography>
  </Box>
);

export default SamDecisionCard;
